/** Tab-local immutable original commands. Proof requests never mark reading. */
import { ApiError, captureRecapBoundary, createRecap, editRecap, deleteRecap, setRecapSeen, markRecapOwnReading } from './api';
import { refreshRecap } from './queryClient';
import type { RecapScope, RecapCommand, RecapCommandResult } from './types';
export type RecapRecoveryState<C extends RecapCommand> =
  | { status: 'ready' | 'sending' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: RecapCommandResult<C> };
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const uuid = (value: unknown): value is string => typeof value === 'string'
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const decimal = (value: unknown, zero = false): value is string => typeof value === 'string'
  && (zero ? /^(0|[1-9][0-9]{0,18})$/ : /^[1-9][0-9]{0,18}$/).test(value) && BigInt(value) <= 9223372036854775807n;
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
function acknowledgement<C extends RecapCommand>(command: C, value: RecapCommandResult): RecapCommandResult<C> {
  const fail = () => { throw new Error('invalid_recap_acknowledgement'); };
  if (!value || typeof value !== 'object') return fail();
  const keys = Object.keys(value).sort().join(',');
  if (command.action === 'delete') {
    if (value.schema_version !== 'recap-delete-result.v1' || value.deleted !== true || keys !== 'deleted,schema_version') return fail();
    return clone(value) as RecapCommandResult<C>;
  }
  if (!('recap_id' in value) || !decimal(value.recap_id) || !uuid(value.revision_id)
    || !Number.isInteger(value.version) || value.version < 1 || value.version > 2147483647
    || (command.action !== 'create' && value.recap_id !== command.recapId)) return fail();
  if (command.action === 'create' || command.action === 'edit') {
    if (value.schema_version !== 'recap-write-result.v1' || !decimal(value.scope_generation, true)
      || keys !== 'recap_id,revision_id,schema_version,scope_generation,version'
      || (command.action === 'create' && (value.version !== 1 || value.scope_generation !== '0'))
      || (command.action === 'edit' && (value.version !== command.body.expected_version + 1
        || value.scope_generation !== command.body.expected_scope_generation
        || same(value.revision_id, command.body.expected_revision_id)))) return fail();
  } else if (command.action === 'seen') {
    if (value.schema_version !== 'recap-seen-result.v1' || keys !== 'player_id,recap_id,revision_id,schema_version,seen,version'
      || typeof value.player_id !== 'string' || !value.player_id || value.seen !== command.body.seen
      || !same(value.revision_id, command.body.expected_published_revision_id)) return fail();
  } else if (value.schema_version !== 'recap-own-read-result.v1' || keys !== 'read_at,recap_id,revision_id,schema_version,version'
    || !same(value.revision_id, command.body.expected_published_revision_id)
    || typeof value.read_at !== 'string' || !Number.isFinite(Date.parse(value.read_at))) return fail();
  return clone(value) as RecapCommandResult<C>;
}
export function captureRecapCommand<C extends RecapCommand>(scope: RecapScope, command: C) {
  const capturedScope = { ...scope }; const original = clone(command); const boundary = captureRecapBoundary(capturedScope);
  let state: RecapRecoveryState<C> = { status: 'ready' }; let unresolved = false;
  let inFlight: Promise<RecapCommandResult<C>> | null = null;
  const assertCurrent = (signal?: AbortSignal) => {
    if (!boundary.current() || signal?.aborted) throw new DOMException('Recap context changed', 'AbortError');
  };
  async function run(signal?: AbortSignal): Promise<RecapCommandResult<C>> {
    state = { status: 'sending' };
    try {
      const intent = clone(original); const options = { scope: { ...capturedScope }, signal };
      const value = intent.action === 'create' ? await createRecap(intent.body, options)
        : intent.action === 'edit' ? await editRecap(intent.recapId, intent.body, options)
          : intent.action === 'delete' ? await deleteRecap(intent.recapId, intent.body, options)
            : intent.action === 'seen' ? await setRecapSeen(intent.recapId, intent.body, options)
              : await markRecapOwnReading(intent.recapId, intent.body, options);
      assertCurrent(signal);
      const result = acknowledgement(original, value);
      state = { status: 'accepted', result }; unresolved = false;
      const id = original.action === 'create' && 'recap_id' in result ? result.recap_id
        : 'recapId' in original ? original.recapId : null;
      void Promise.resolve().then(() => {
        if (boundary.current() && id !== null) return refreshRecap(capturedScope, id, original.action === 'delete');
      }).catch(() => {});
      return clone(result);
    } catch (error) {
      const code = error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail ? error.detail.code : null;
      // Missing/retired receipts, transport failure or invalid ACKs cannot prove
      // whether the original committed. Later denial preserves that uncertainty.
      if (code === 'operation_retired' || code === 'operation_unresolved'
        || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) unresolved = true;
      state = code === 'operation_retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      throw error;
    } finally { inFlight = null; }
  }
  return {
    getState(): RecapRecoveryState<C> { return boundary.current() ? clone(state) : { status: 'unavailable' }; },
    getIntent(): C | null { return boundary.current() ? clone(original) : null; },
    send(signal?: AbortSignal): Promise<RecapCommandResult<C>> {
      try { assertCurrent(signal); } catch (error) { return Promise.reject(error); }
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve(clone(state.result));
      inFlight = run(signal); return inFlight;
    },
    dispose: () => boundary.dispose(),
  };
}
